import { Card, Flex, SimpleTable, Switcher, Typography } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "./../../urls";

const { Title, Text } = Typography;

const SwitcherPage = () => {
  const [swith, setSwitch] = useState(false);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Switcher</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Switcher component in Djuno Design is a simple, customizable
            toggle switch. It allows users to switch between two states,
            typically on and off. The Switcher component supports various sizes,
            loading states, and a disabled state, providing flexibility for
            different use cases in forms, settings, and more.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Switcher component offers the following customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Size Variants
                </Text>
                : Available in small, medium, and large sizes, the Switcher can
                be adapted to fit different interface requirements.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Disabled State
                </Text>
                : The Switcher can be disabled, preventing any interaction or
                state changes. It visually indicates its disabled state by
                altering the background and cursor.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading Indicator
                </Text>
                : When in a loading state, the Switcher can display a loading
                spinner inside the toggle knob, customizable via the
                loadingSetting prop.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              title="Swither"
              codeViewerProps={{
                code: SwitcherExample1,
              }}
            >
              <Flex direction="col" className="gap-5">
                <div>
                  <Text>sizes:</Text>
                  <Flex className="gap-5 w-full">
                    <Flex direction="col">
                      <Text uiType="secondary" size="sm">
                        small
                      </Text>
                      <Switcher
                        value={swith}
                        onChange={setSwitch}
                        uiSize="small"
                      />
                    </Flex>
                    <Flex direction="col">
                      <Text uiType="secondary" size="sm">
                        medium
                      </Text>
                      <Switcher value={swith} onChange={setSwitch} />
                    </Flex>
                    <Flex direction="col">
                      <Text uiType="secondary" size="sm">
                        large
                      </Text>
                      <Switcher
                        value={swith}
                        onChange={setSwitch}
                        uiSize="large"
                      />
                    </Flex>
                  </Flex>
                </div>
                <div>
                  <Text>disabled:</Text>
                  <Flex items="center" className="gap-5 w-full">
                    <Switcher value={swith} onChange={setSwitch} disabled />
                  </Flex>
                </div>
                <div>
                  <Text>loading:</Text>
                  <Flex items="center" className="gap-5 w-full">
                    <Switcher value={swith} onChange={setSwitch} loading />
                  </Flex>
                </div>
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* value */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">value</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls whether the switch is in the "on" or "off"
                        position.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* onChange */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">onChange</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Callback fired when the switch is toggled. Passes the
                        new state as an argument.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`(value: boolean)=>void`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* disabled */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">disabled</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Disables the switch, preventing user interaction and
                        indicating a disabled state visually.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        When true, shows a loading spinner inside the switch
                        knob, indicating that an action is in progress.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* loadingType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Specifies the type of loading spinner to display.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls the size of the Switcher. Affects both the
                        overall switch size and the knob.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const SwitcherExample1 = `<Flex direction="col" className="gap-5">
  <div>
    <Text>sizes:</Text>
    <Flex className="gap-5 w-full">
      <Flex direction="col">
        <Text uiType="secondary" size="sm">
          small
        </Text>
        <Switcher
          value={swith}
          onChange={setSwitch}
          size="small"
        />
      </Flex>
      <Flex direction="col">
        <Text uiType="secondary" size="sm">
          medium
        </Text>
        <Switcher value={swith} onChange={setSwitch} />
      </Flex>
      <Flex direction="col">
        <Text uiType="secondary" size="sm">
          large
        </Text>
        <Switcher
          value={swith}
          onChange={setSwitch}
          size="large"
        />
      </Flex>
    </Flex>
  </div>
  <div>
    <Text>disabled:</Text>
    <Flex items="center" className="gap-5 w-full">
      <Switcher value={swith} onChange={setSwitch} disabled />
    </Flex>
  </div>
  <div>
    <Text>loading:</Text>
    <Flex items="center" className="gap-5 w-full">
      <Switcher value={swith} onChange={setSwitch} loading />
    </Flex>
  </div>
</Flex>`;
export default SwitcherPage;
